import { ENGINE_URL, BASE_URL } from "./service.config.js";
import { clearUserDirectoryCache } from "./userService.js";

/**
 * Unified API Client for TRDR Go Trading Engine (Live & Simulation)
 */
export const TradingService = {
  /**
   * Cancel single active order
   */
  async cancelOrder(ord) {
    if (!ord) throw new Error("Invalid order target");
    const payload = {
      client_id: ord.client && ord.client !== "-" ? ord.client : "",
      bse_order_no: ord.bseOrderNo || "",
      order_tag: ord.orderTag || ord.id || "",
      scrip_code: ord.scripCode || ord.symbol || "",
    };

    const res = await fetch(`${ENGINE_URL}/cancel`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      const errJson = await res.json().catch(() => ({}));
      throw new Error(errJson.error || `HTTP ${res.status}`);
    }
    return true;
  },

  /**
   * Bulk cancel list of active orders
   */
  async bulkCancelOrders(orders = []) {
    if (orders.length === 0) return { succeeded: 0, failed: 0 };

    const results = await Promise.allSettled(
      orders.map((ord) =>
        fetch(`${ENGINE_URL}/cancel`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            client_id: ord.client && ord.client !== "-" ? ord.client : "",
            bse_order_no: ord.bseOrderNo || "",
            order_tag: ord.orderTag || ord.id || "",
            scrip_code: ord.scripCode || ord.symbol || "",
          }),
        })
      )
    );

    const succeeded = results.filter((r) => r.status === "fulfilled" && r.value.ok).length;
    const failed = orders.length - succeeded;
    return { succeeded, failed };
  },

  /**
   * Square off a single client position
   */
  async squareOffPosition({ ucc, symbol, isSim = false }) {
    if (!ucc) throw new Error("Client UCC is required");

    let res;
    if (isSim) {
      res = await fetch(`${ENGINE_URL}/sim/squareoff`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ucc, scrip_code: symbol || "" }),
      });
    } else {
      res = await fetch(`${ENGINE_URL}/client/${encodeURIComponent(ucc)}/squareoff`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ symbol: symbol || "" }),
      });
    }

    if (!res.ok) {
      const errData = await res.json().catch(() => ({}));
      throw new Error(errData.error || `HTTP ${res.status}`);
    }
    return true;
  },

  /**
   * Bulk square off open positions
   */
  async bulkSquareOff({ positions = [], isSim = false }) {
    if (positions.length === 0) return { succeeded: 0, failed: 0 };

    const results = await Promise.allSettled(
      positions.map((pos) => {
        const client = pos.client || pos.ucc;
        if (isSim) {
          return fetch(`${ENGINE_URL}/sim/squareoff`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ ucc: client, scrip_code: pos.symbol }),
          });
        } else {
          return fetch(`${ENGINE_URL}/client/${encodeURIComponent(client)}/squareoff`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ symbol: pos.symbol }),
          });
        }
      })
    );

    const succeeded = results.filter((r) => r.status === "fulfilled" && r.value.ok).length;
    const failed = positions.length - succeeded;
    return { succeeded, failed };
  },

  /**
   * Assign or update UCC / client tag mapping
   */
  async assignUccTag(payload, isSim = false) {
    const endpoint = isSim
      ? `${ENGINE_URL}/sim/client-tag/assign`
      : `${ENGINE_URL}/client-tag/assign`;

    const res = await fetch(endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      const errJson = await res.json().catch(() => ({}));
      throw new Error(errJson.error || `HTTP ${res.status}`);
    }

    clearUserDirectoryCache();
    return await res.json().catch(() => ({}));
  },

  /**
   * Adjust or top-up virtual balance in sandbox
   */
  async adjustVirtualBalance({ ucc, virtual_balance, amount_delta, action = "SET" }) {
    const payload = { ucc };
    if (amount_delta !== undefined) {
      payload.amount_delta = Number(amount_delta);
    } else {
      payload.virtual_balance = Number(virtual_balance);
      payload.action = action;
    }

    const res = await fetch(`${ENGINE_URL}/sim/balance/adjust`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      const errJson = await res.json().catch(() => ({}));
      throw new Error(errJson.error || `HTTP ${res.status}`);
    }
    return await res.json();
  },

  /**
   * Toggle Algorithmic Auto-Trade (Unified Live & Simulation Router)
   * Routes to POST /api/v1/engine/client/{ucc}/autotrade
   */
  async toggleAutoTrade(ucc, enabled, strategies = null, multiplier = null) {
    const cleanUcc = (ucc || "").trim().toUpperCase();
    const payload = {
      enabled: Boolean(enabled),
    };
    if (multiplier !== null && multiplier !== undefined && !isNaN(Number(multiplier))) {
      payload.multiplier = Number(multiplier);
    }
    if (Array.isArray(strategies)) {
      payload.strategies = strategies;
    }

    const res = await fetch(`${ENGINE_URL}/client/${encodeURIComponent(cleanUcc)}/autotrade`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      const errJson = await res.json().catch(() => ({}));
      throw new Error(errJson.error || `HTTP ${res.status}: Failed to toggle auto-trade`);
    }

    // Invalidate in-memory directory cache to guarantee fresh state on re-render
    clearUserDirectoryCache();
    return await res.json().catch(() => ({ success: true }));
  },

  /**
   * Fetch client trading and sizing configuration
   */
  async fetchClientConfig(ucc, isSim = false) {
    const cleanUcc = (ucc || "").trim().toUpperCase();
    const endpoint = isSim
      ? `${ENGINE_URL}/sim/client/${cleanUcc}/config`
      : `${ENGINE_URL}/client/${cleanUcc}/config`;

    const res = await fetch(endpoint);
    if (!res.ok) {
      throw new Error(`Failed to load config (HTTP ${res.status})`);
    }
    return await res.json();
  },

  /**
   * Save client F1F2 native tier & sizing configuration
   */
  async saveClientConfig(ucc, configPayload, isSim = false) {
    const cleanUcc = (ucc || "").trim().toUpperCase();
    const endpoint = isSim
      ? `${ENGINE_URL}/sim/client/${encodeURIComponent(cleanUcc)}/config`
      : `${ENGINE_URL}/client/${encodeURIComponent(cleanUcc)}/config`;

    const res = await fetch(endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(configPayload),
    });

    if (!res.ok) {
      const errJson = await res.json().catch(() => ({}));
      throw new Error(errJson.error || `HTTP ${res.status}`);
    }
    clearUserDirectoryCache();
    return true;
  },

  /**
   * Bulk update client configuration (Task 4.7)
   */
  async bulkUpdateClientConfig(payload) {
    const res = await fetch(`${ENGINE_URL}/clients/bulk-config`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      const errJson = await res.json().catch(() => ({}));
      throw new Error(errJson.error || `HTTP ${res.status}: Failed to update clients in bulk`);
    }
    clearUserDirectoryCache();
    return await res.json().catch(() => ({ success: true }));
  },

  /**
   * Per-Scrip Emergency Square-Off (Task 4.5, strategyapisqr4 parity)
   */
  async squareOffScrip(symbol) {
    const sym = (symbol || "").trim().toUpperCase();
    if (!sym) throw new Error("Scrip symbol is required");
    const res = await fetch(`${ENGINE_URL}/square-off/scrip/${encodeURIComponent(sym)}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
    });
    if (!res.ok) {
      const errJson = await res.json().catch(() => ({}));
      throw new Error(errJson.error || `HTTP ${res.status}: Failed to square off ${sym}`);
    }
    return await res.json().catch(() => ({ success: true }));
  },

  /**
   * Send Manual Extra Signal (Task 4.6, ExtraManualSignal parity)
   */
  async sendManualSignal(signalData) {
    const res = await fetch(`${ENGINE_URL}/signal/manual`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(signalData),
    });
    if (!res.ok) {
      const errJson = await res.json().catch(() => ({}));
      throw new Error(errJson.error || `HTTP ${res.status}: Failed to send manual signal`);
    }
    return await res.json().catch(() => ({ success: true }));
  },

  /**
   * Fetch F1F2 Company Array Scrip Matrix (Task 4.4, /s3s4 parity)
   */
  async fetchCompanyArray({ search = "", limit = 100, offset = 0, trading_st } = {}) {
    let url = `${BASE_URL}/api/v1/master-data/f1f2-company-array?limit=${limit}&offset=${offset}`;
    if (search) url += `&search=${encodeURIComponent(search)}`;
    if (trading_st !== undefined && trading_st !== null) url += `&trading_st=${trading_st}`;
    const res = await fetch(url);
    if (!res.ok) {
      const errJson = await res.json().catch(() => ({}));
      throw new Error(errJson.error || `HTTP ${res.status}: Failed to load scrip matrix`);
    }
    return await res.json();
  },

  /**
   * Update F1F2 Company Array Scrip (Task 4.4, /s3s4 parity)
   */
  async updateCompanyArrayScrip(id, data) {
    const res = await fetch(`${BASE_URL}/api/v1/master-data/f1f2-company-array/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });
    if (!res.ok) {
      const errJson = await res.json().catch(() => ({}));
      throw new Error(errJson.error || `HTTP ${res.status}: Failed to update scrip`);
    }
    return await res.json();
  },

  /**
   * Fetch active gateway and engine status
   */
  async fetchGatewayConfig() {
    const res = await fetch(`${ENGINE_URL}/gateway/config`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  },
};

export default TradingService;
